import { useState, useEffect, useRef } from "react";
import { isOuraUIEnabled } from "../utils/features";

const P = {
  bg: "#F8F6F2",
  bg2: "#F0EDE6",
  surface: "#F0EDE6",
  border: "rgba(28,24,20,0.08)",
  borderMd: "rgba(28,24,20,0.13)",
  gold: "#9E8456",
  goldDim: "rgba(158,132,86,0.70)",
  goldLt: "rgba(158,132,86,0.09)",
  goldBd: "rgba(158,132,86,0.28)",
  goldBr: "rgba(158,132,86,0.40)",
  goldDot: "rgba(158,132,86,0.5)",
  text: "#1C1814",
  body: "rgba(28,24,20,0.60)",
  eyebrow: "rgba(28,24,20,0.36)",
  ink4: "rgba(28,24,20,0.20)",
  tension: "rgba(28,24,20,0.36)",
  callout: "rgba(28,24,20,0.45)",
  cardInsight: "rgba(28,24,20,0.4)",
  textFaint: "rgba(28,24,20,0.20)",
  divider: "0.5px solid rgba(28,24,20,0.08)",
};

const display = "'Instrument Serif', Georgia, serif";
const serif = "'Playfair Display', Georgia, serif";
const sans = "'DM Sans', sans-serif";

// ----- Intersection Observer hook for staggered reveals -----
function useScrollReveal() {
  const observed = useRef(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !observed.current.has(entry.target)) {
            observed.current.add(entry.target);
            const stagger = entry.target.dataset.stagger || "0";
            entry.target.style.transitionDelay = `${stagger}ms`;
            entry.target.classList.add("rv-visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -80px 0px" }
    );
    const els = document.querySelectorAll(".rv");
    els.forEach((el) => observer.observe(el));

    const fallback = setTimeout(() => {
      document.querySelectorAll(".rv:not(.rv-visible)").forEach((el) => {
        el.classList.add("rv-visible");
      });
    }, 6000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);
}

// AppPreview, Ticker, EngineSection removed in hero restructure

// ----- Main -----
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useScrollReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const tensions = [
    "The wins are real. The emptiness after them is too.",
    "You can explain the problem exactly. You just can't close it.",
    "You've put in the work. Something still feels off.",
  ];

  return (
    <>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${P.bg}; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        .landing { font-family: ${sans}; color: ${P.text}; background: ${P.bg}; }
        .landing a { color: inherit; text-decoration: none; }
        .section-pad { padding: 120px 24px; max-width: 1180px; margin: 0 auto; }

        .dot-grid {
          background-image: radial-gradient(circle, rgba(28,24,20,0.045) 1px, transparent 1px);
          background-size: 32px 32px;
        }

        .hero-el { animation: fadeUp 0.8s ease both; }
        .hero-d1 { animation-delay: 0.15s; }
        .hero-d2 { animation-delay: 0.3s; }
        .hero-d3 { animation-delay: 0.45s; }
        .hero-d4 { animation-delay: 0.6s; }
        .hero-d5 { animation-delay: 0.75s; }

        .rv {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .rv.rv-visible {
          opacity: 1;
          transform: translateY(0);
        }

        .nav-link {
          background:none; border:none; cursor:pointer;
          font-size:0.68rem; font-weight:400; color:${P.eyebrow};
          font-family:${sans}; text-transform:uppercase; letter-spacing:0.12em;
          transition:color 0.3s; padding:0;
        }
        .nav-link:hover { color:rgba(28,24,20,0.7); }

        .cta-primary {
          background: #1C1814;
          color: #F8F6F2;
          border: none;
          border-radius: 100px;
          cursor: pointer;
          font-family: ${sans};
          font-weight: 400;
          letter-spacing: 0.04em;
          transition: opacity 0.25s;
        }
        .cta-primary:hover { opacity: 0.85; }

        .cta-secondary {
          background: transparent;
          color: rgba(28,24,20,0.4);
          border: 0.5px solid ${P.border};
          border-radius: 100px;
          cursor: pointer;
          font-family: ${sans};
          transition: color 0.25s, border-color 0.25s;
        }
        .cta-secondary:hover {
          color: rgba(28,24,20,0.65);
          border-color: ${P.borderMd};
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-el, .rv { animation: none !important; transition: none !important; opacity: 1 !important; transform: none !important; }
          /* reduced motion — no ticker */
        }

        @media (max-width: 768px) {
          .hero-grid { flex-direction: column !important; }
          .hero-right { display: none !important; }
          .hero-ring-bg { display: none !important; }
          .section-pad { padding: 80px 20px; }
          .landing-nav {
            height: 52px !important;
            padding: 0 20px !important;
          }
          .hero-section { padding-top: 100px !important; min-height: 90vh !important; }
          .hero-headline { font-size: clamp(2.4rem, 8vw, 3.6rem) !important; }
          .problem-heading { font-size: 42px !important; }
          /* Practitioner section — stack the 3 feature columns and
             tighten the side padding on narrow viewports. */
          .practitioner-section { padding: 80px 20px !important; }
          .practitioner-cols {
            grid-template-columns: 1fr !important;
            gap: 36px !important;
          }
          .practitioner-headline { font-size: 32px !important; }
        }
      `}</style>

      <div className="landing">

        {/* ===== NAV ===== */}
        <nav className="landing-nav" style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
          padding: "0 36px", height: 64,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          transition: "all 0.4s",
          background: scrolled ? "rgba(248,246,242,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(24px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(24px)" : "none",
          borderBottom: scrolled ? `0.5px solid ${P.border}` : "0.5px solid transparent",
        }}>
          <img src="/logo-gold.png" alt="Haiven" style={{ height: 30, width: "auto" }} />
          {/* Login-only nav — no Request Access / join button. The hero
              is light now, so the link color is flat (no scroll-state
              swap needed for legibility over a dark hero). */}
          <a href="/auth" className="nav-login-link" style={{
            fontSize: 13, letterSpacing: "0.04em",
            color: "rgba(28,24,20,0.45)",
            textDecoration: "none", cursor: "pointer",
            transition: "color 0.3s",
          }}>Login</a>
        </nav>

        {/* ===== HERO — Full viewport, light, founding-cohort copy ===== */}
        <section className="hero-section" style={{
          minHeight: "100vh",
          background: P.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", overflow: "hidden",
          paddingTop: 64,
        }}>
          {/* Subtle ring background element */}
          <div className="hero-ring-bg" style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: "60%", maxWidth: 600,
            opacity: 0.07,
            pointerEvents: "none",
            zIndex: 0,
          }}>
            <img src="/ring-hero.png" alt="" style={{ width: "100%", height: "auto" }} />
          </div>

          <div style={{
            maxWidth: 680, margin: "0 auto", padding: "0 32px",
            textAlign: "center", position: "relative", zIndex: 1,
          }}>
            {/* Eyebrow */}
            <div className="hero-el hero-d1" style={{
              fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
              color: "rgba(158,132,86,0.65)", fontWeight: 400, marginBottom: 40,
            }}>The beginning. &nbsp;Something different.</div>

            {/* Headline */}
            <h1 className="hero-el hero-d2 hero-headline" style={{
              fontFamily: display,
              fontSize: "clamp(3rem, 6vw, 4.8rem)",
              fontWeight: 400, lineHeight: 1.10,
              color: P.text, marginBottom: 0,
            }}>
              You're executing everything.
            </h1>

            {/* Headline line 2 — gold italic */}
            <p className="hero-el hero-d2" style={{
              fontFamily: display,
              fontStyle: "italic",
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              color: "#9E8456",
              lineHeight: 1.20,
              marginTop: 12, marginBottom: 0,
            }}>
              The gap is still widening.
            </p>

            {/* Gold rule */}
            <div className="hero-el hero-d3" style={{
              width: 32, height: 1, background: "rgba(158,132,86,0.35)",
              margin: "40px auto",
            }} />

            {/* Subheadline */}
            <p className="hero-el hero-d3" style={{
              fontSize: 16, color: "rgba(28,24,20,0.60)", fontWeight: 300,
              lineHeight: 1.80, maxWidth: 560, margin: "0 auto 40px",
            }}>
              Haiven measures the gap between how your life looks and how it actually feels — and shows you where it's coming from.
            </p>

            {/* CTA */}
            <div className="hero-el hero-d4" style={{ marginBottom: 20 }}>
              <button className="cta-primary" onClick={() => scrollTo("early-access")} style={{
                padding: "16px 40px", fontSize: "0.85rem",
              }}>Apply for the founding cohort</button>
            </div>

            {/* Secondary line */}
            <div className="hero-el hero-d5" style={{
              fontSize: 13, color: "rgba(28,24,20,0.36)", fontWeight: 300,
              lineHeight: 1.7, maxWidth: 440, margin: "0 auto",
            }}>
              15 spots. &nbsp;By application.
            </div>
          </div>
        </section>

        {/* ===== OURA BANNER ===== (hidden in the native iOS build while
            VITE_IOS_OURA_ENABLED is off, see utils/features.js) */}
        {isOuraUIEnabled() && (
        <section style={{ background: "#1C1814", padding: "64px 40px", width: "100%", textAlign: "center" }}>
          <div style={{ maxWidth: 600, margin: "0 auto" }}>
            <div className="rv" style={{ fontSize: 10, color: "rgba(158,132,86,0.65)", textTransform: "uppercase", letterSpacing: "0.20em", marginBottom: 20 }}>
              Wearable Integration
            </div>
            <div className="rv" data-stagger="80" style={{ marginBottom: 24 }}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="18" stroke="#9E8456" strokeWidth="1.5" fill="none" opacity="0.8" />
                <circle cx="24" cy="24" r="10" stroke="#9E8456" strokeWidth="1" fill="none" opacity="0.4" />
              </svg>
            </div>
            <h2 className="rv" data-stagger="160" style={{ fontFamily: display, fontSize: 36, color: "#F8F6F2", fontWeight: 400, marginBottom: 16 }}>
              Connect your Oura.
            </h2>
            <p className="rv" data-stagger="240" style={{ fontSize: 15, lineHeight: 1.72, color: "rgba(248,246,242,0.55)", maxWidth: 480, margin: "0 auto 28px" }}>
              Your sleep, activity, and energy data flows directly into your Body domain — automatically. No manual input. Just signal.
            </p>
            <div className="rv" data-stagger="320" style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
              {["Automatic", "Passive", "Real-time"].map((tag) => (
                <span key={tag} style={{ fontSize: 11, padding: "5px 14px", borderRadius: 20, background: "rgba(158,132,86,0.12)", border: "0.5px solid rgba(158,132,86,0.30)", color: "rgba(158,132,86,0.85)" }}>{tag}</span>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* ===== FOUNDER MOMENT ===== */}
        <section style={{ background: P.bg, borderTop: "0.5px solid rgba(28,24,20,0.07)" }}>
          <div style={{ maxWidth: 560, margin: "0 auto", padding: "140px 40px", textAlign: "center" }}>
            <p className="rv" style={{
              fontFamily: display,
              fontStyle: "italic",
              fontSize: "clamp(1.2rem, 2vw, 1.45rem)",
              color: P.gold,
              lineHeight: 1.7,
              margin: 0,
            }}>
              I built this because I kept watching high-performing people who knew what to do and still didn't do it. Not from laziness. From fragmentation. Their sleep tracker said one thing. Their mood said another. Their calendar said something else. None of it talked to each other. And none of it told them what to actually do next. Our modern lives have left us swimming in data and the average person has no idea what is really the most important thing and what is actually moving the needle. Haiven is what I wished existed — and my attempt to build it.
            </p>
          </div>
        </section>

        {/* ===== PROBLEM — Cinematic Scroll ===== */}
        <section style={{ background: P.bg, borderTop: "0.5px solid rgba(28,24,20,0.07)" }}>
          <div style={{ maxWidth: 680, margin: "0 auto", padding: "80px 40px", textAlign: "left" }}>
            {/* Step 1 — Label + Headline */}
            <div className="rv" style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#9E8456", fontWeight: 500, marginBottom: 32, textAlign: "left" }}>
              The Problem
            </div>
            <h2 className="rv problem-heading" data-stagger="100" style={{ fontFamily: display, fontSize: 64, lineHeight: 1.10, fontWeight: 400, marginBottom: 48, textAlign: "left" }}>
              <span style={{ color: "#1C1814" }}>You're not broken.</span><br />
              <span style={{ fontStyle: "italic", color: "#9E8456" }}>You're fragmented.</span>
            </h2>

            {/* Step 2 — Body */}
            <div className="rv" data-stagger="250" style={{ fontSize: 14, color: "rgba(28,24,20,0.60)", lineHeight: 1.8, maxWidth: 540, marginBottom: 48, textAlign: "left" }}>
              <p style={{ marginBottom: 20, textAlign: "left" }}>
                High-functioning adults don't lack data — nothing connects what they're feeling to what's causing it. Your sleep tracker, journaling app, and habit tracker each tell a fragment of the story. None of them talk to each other.
              </p>
              <p style={{ textAlign: "left" }}>
                The result is noise dressed up as insight. You're measuring everything and understanding nothing.
              </p>
            </div>

            {/* Step 3 — Label */}
            <div className="rv" data-stagger="400" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#9E8456", fontWeight: 500, marginBottom: 20, textAlign: "left" }}>
              What brought you here
            </div>

            {/* Step 4 — Tension lines */}
            <div style={{ width: "100%", textAlign: "left" }}>
              {tensions.map((t, i) => (
                <div key={i} className="rv" data-stagger={500 + i * 200} style={{
                  textAlign: "left",
                  marginBottom: 22, paddingLeft: 18,
                  borderLeft: "1.5px solid rgba(158,132,86,0.40)",
                }}>
                  <span style={{ fontFamily: display, fontStyle: "italic", fontSize: 16, color: "rgba(28,24,20,0.60)", lineHeight: 1.55 }}>{t}</span>
                </div>
              ))}
            </div>

            {/* Step 5 — Gut punch */}
            <div style={{ textAlign: "left" }}>
              <p className="rv" data-stagger="1200" style={{ fontFamily: display, fontStyle: "italic", fontSize: 19, color: "rgba(28,24,20,0.60)", lineHeight: 1.6, marginBottom: 12, textAlign: "left" }}>
                That gap doesn't fix itself — it grows.
              </p>
              <p className="rv" data-stagger="1600" style={{ fontFamily: display, fontStyle: "normal", fontSize: 24, color: "#1C1814", fontWeight: 500, lineHeight: 1.4, textAlign: "left" }}>
                We call it <span style={{ fontStyle: "italic", color: "#9E8456" }}>coherence debt</span>, and it compounds.
              </p>
            </div>
          </div>
        </section>

        {/* ===== WHAT IT IS — Plain-language block ===== */}
        <section style={{ background: P.bg, borderTop: "0.5px solid rgba(28,24,20,0.07)" }}>
          <div style={{ maxWidth: 600, margin: "0 auto", padding: "100px 40px", textAlign: "center" }}>
            <p className="rv" style={{ fontFamily: display, fontSize: 22, color: "rgba(28,24,20,0.60)", lineHeight: 1.6, marginBottom: 28 }}>
              Haiven checks in with you every day — a few minutes, in your own words.
            </p>
            <p className="rv" data-stagger="120" style={{ fontFamily: display, fontSize: 22, color: "rgba(28,24,20,0.60)", lineHeight: 1.6, marginBottom: 28 }}>
              Over time, it shows you the patterns underneath: where the gap comes from, what feeds it, what closes it.
            </p>
            <p className="rv" data-stagger="240" style={{ fontFamily: display, fontStyle: "italic", fontSize: 22, color: "#9E8456", lineHeight: 1.6, margin: 0 }}>
              Like a fitness tracker — but for the part of you nothing else tracks.
            </p>
          </div>
        </section>

        {/* ===== HOW IT WORKS — Vertical Steps ===== */}
        <section id="how" style={{ background: P.bg2, borderTop: "0.5px solid rgba(28,24,20,0.07)" }}>
          <div style={{ padding: "80px 40px", maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
            <div className="rv" style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(158,132,86,0.65)", fontWeight: 400, marginBottom: 16 }}>
              How It Works
            </div>
            <h2 className="rv how-heading" data-stagger="80" style={{ fontFamily: display, fontSize: "clamp(2rem, 4.5vw, 3.2rem)", color: P.text, marginBottom: 16, fontWeight: 400 }}>
              Simple input.{" "}
              <span style={{ fontStyle: "italic", color: P.gold }}>Deep signal.</span>
            </h2>
            <p className="rv" data-stagger="200" style={{ fontSize: 14, color: "rgba(28,24,20,0.36)", maxWidth: 520, margin: "0 auto 56px", lineHeight: 1.75 }}>
              A closed feedback loop. Your data builds your signal. Your signal builds your intelligence. Your intelligence closes the gap.
            </p>

            {/* Step 01 */}
            <div className="rv" style={{ display: "flex", textAlign: "left" }}>
              <div style={{ width: 64, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <span style={{ fontFamily: display, fontSize: 13, color: "#9E8456", letterSpacing: "0.16em" }}>01</span>
                <div style={{ width: 1, height: 60, background: "rgba(158,132,86,0.25)", marginTop: 8 }} />
              </div>
              <div style={{ flex: 1, paddingLeft: 24, paddingBottom: 48 }}>
                <div style={{ fontSize: 10, color: "rgba(158,132,86,0.55)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 8 }}>Input</div>
                <h3 style={{ fontFamily: display, fontSize: 26, color: "#1C1814", marginBottom: 10, fontWeight: 400 }}>Daily check-in</h3>
                <p style={{ fontSize: 14, color: "rgba(28,24,20,0.60)", lineHeight: 1.78, marginBottom: 14 }}>
                  2 minutes. Three domains. A few guided questions across sleep, energy, emotional state, and follow-through. Answer in your own words — a sentence is enough.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {["2 min", "Daily", "Structured"].map((tag) => (
                    <span key={tag} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: "#E8E4DC", border: "0.5px solid rgba(28,24,20,0.13)", color: "rgba(28,24,20,0.45)", fontWeight: 400 }}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 02 */}
            <div className="rv" style={{ display: "flex", textAlign: "left" }}>
              <div style={{ width: 64, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <span style={{ fontFamily: display, fontSize: 13, color: "#9E8456", letterSpacing: "0.16em" }}>02</span>
                <div style={{ width: 1, height: 60, background: "rgba(158,132,86,0.25)", marginTop: 8 }} />
              </div>
              <div style={{ flex: 1, paddingLeft: 24, paddingBottom: 48 }}>
                <div style={{ fontSize: 10, color: "rgba(158,132,86,0.55)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 8 }}>Measurement</div>
                <h3 style={{ fontFamily: display, fontSize: 26, color: "#1C1814", marginBottom: 10, fontWeight: 400 }}>The Signal Score</h3>
                <p style={{ fontSize: 14, color: "rgba(28,24,20,0.60)", lineHeight: 1.78, marginBottom: 14 }}>
                  Your Signal score is Haiven's proprietary composite — the first score built to reflect body, mind, and behavior together. One number. Three domains. Updated after every check-in.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                  {["Proprietary", "Composite", "Real-time"].map((tag) => (
                    <span key={tag} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: "#E8E4DC", border: "0.5px solid rgba(28,24,20,0.13)", color: "rgba(28,24,20,0.45)", fontWeight: 400 }}>{tag}</span>
                  ))}
                </div>
                <div style={{ fontFamily: display, fontSize: 13, color: "rgba(158,132,86,0.70)", letterSpacing: "0.04em", marginTop: 14 }}>
                  Body 82 &nbsp;&middot;&nbsp; Mind 71 &nbsp;&middot;&nbsp; Behavior 68 &nbsp;&rarr;&nbsp; <span style={{ color: "#9E8456" }}>SIGNAL 74</span>
                </div>
              </div>
            </div>

            {/* Step 03 */}
            <div className="rv" style={{ display: "flex", textAlign: "left" }}>
              <div style={{ width: 64, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <span style={{ fontFamily: display, fontSize: 13, color: "#9E8456", letterSpacing: "0.16em" }}>03</span>
              </div>
              <div style={{ flex: 1, paddingLeft: 24, paddingBottom: 0 }}>
                <div style={{ fontSize: 10, color: "rgba(158,132,86,0.55)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 8 }}>Intelligence</div>
                <h3 style={{ fontFamily: display, fontSize: 26, color: "#1C1814", marginBottom: 10, fontWeight: 400 }}>The feed</h3>
                <p style={{ fontSize: 14, color: "rgba(28,24,20,0.60)", lineHeight: 1.78, marginBottom: 14 }}>
                  This is where Haiven becomes intelligent. The feed reads cross-domain patterns your individual trackers can never see — correlations between your HRV, your emotional state, and your follow-through rate across days and weeks. What surfaces isn't generic wellness copy. It's your numbers, your patterns, your next move.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                  {["AI-powered", "Pattern-aware", "Personalized"].map((tag) => (
                    <span key={tag} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20, background: "#E8E4DC", border: "0.5px solid rgba(28,24,20,0.13)", color: "rgba(28,24,20,0.45)", fontWeight: 400 }}>{tag}</span>
                  ))}
                </div>
                <div style={{ background: "#F0EDE6", border: "0.5px solid rgba(28,24,20,0.09)", borderLeft: "2px solid rgba(158,132,86,0.40)", borderRadius: "0 6px 6px 0", padding: "12px 14px", marginTop: 14 }}>
                  <p style={{ fontSize: 12, color: "rgba(28,24,20,0.55)", lineHeight: 1.6, margin: 0 }}>
                    Your rest is trending up over 5 days — lining up with your evening routine.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ===== PRACTITIONER SHARING — NEW ===== */}
        <section className="practitioner-section" style={{
          background: "#F8F6F2",
          borderTop: "0.5px solid rgba(28,24,20,0.07)",
          padding: "100px 40px",
        }}>
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            {/* Label */}
            <div className="rv" data-stagger="0" style={{
              color: "rgba(158,132,86,0.65)", fontSize: 10,
              textTransform: "uppercase", letterSpacing: "0.20em",
              textAlign: "center", marginBottom: 40,
            }}>
              Share Your Progress
            </div>

            {/* Headline — two lines, centered */}
            <h2 className="rv practitioner-headline" data-stagger="150" style={{
              fontFamily: display, fontWeight: 400,
              textAlign: "center", marginBottom: 28,
            }}>
              <span style={{ display: "block", fontSize: 42, color: "#1C1814", fontStyle: "normal", lineHeight: 1.15 }}>
                Bring the full picture.
              </span>
              <span style={{ display: "block", fontSize: 42, color: "#9E8456", fontStyle: "italic", lineHeight: 1.15 }}>
                Not just what you remember.
              </span>
            </h2>

            {/* Body */}
            <div className="rv" data-stagger="300" style={{
              fontSize: 15, lineHeight: 1.78,
              color: "rgba(28,24,20,0.55)",
              textAlign: "center",
              maxWidth: 600, margin: "0 auto 56px",
            }}>
              <p style={{ marginBottom: 20 }}>
                Haiven turns your data into a shareable summary — patterns, trends, and domain scores — that you can share with anyone you choose.
              </p>
              <p>
                You can share where you actually are — not just where you remember being, or what you can put into words in the moment. What your own data shows.
              </p>
            </div>

            {/* Three feature columns */}
            <div className="rv practitioner-cols" data-stagger="450" style={{
              display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24, maxWidth: 720, margin: "0 auto 48px",
            }}>
              {[
                {
                  headline: "Insight reports",
                  body: "Auto-generated summaries of your Signal patterns — shareable anytime.",
                },
                {
                  headline: "Domain breakdowns",
                  body: "Body, Mind, and Behavior scores over time — with the cross-domain correlations that matter.",
                },
                {
                  headline: "Progress over time",
                  body: "Share your signal trend, not just today's score. Progress is visible. Patterns are named.",
                },
              ].map((col) => (
                <div key={col.headline} style={{ textAlign: "center" }}>
                  <div style={{
                    width: 24, height: 1,
                    background: "rgba(158,132,86,0.35)",
                    margin: "0 auto 14px",
                  }} />
                  <h3 style={{
                    fontFamily: display, fontSize: 18,
                    color: "#1C1814", marginBottom: 8, fontWeight: 400,
                  }}>
                    {col.headline}
                  </h3>
                  <p style={{
                    fontSize: 13, lineHeight: 1.68,
                    color: "rgba(28,24,20,0.50)",
                  }}>
                    {col.body}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* ===== VISION ===== */}
        <section style={{ background: P.bg2, borderTop: "0.5px solid rgba(28,24,20,0.07)" }}>
          <div style={{ padding: "100px 40px", maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
            <div className="rv" style={{ fontSize: 11, letterSpacing: "0.20em", textTransform: "uppercase", color: "rgba(158,132,86,0.65)", fontWeight: 400, marginBottom: 40, textAlign: "center" }}>The Vision</div>
            <h2 style={{ fontFamily: display, fontWeight: 400, marginBottom: 48, textAlign: "center" }}>
              <span className="rv" data-stagger="80" style={{ display: "block", fontSize: 42, lineHeight: 1.15, color: "#1C1814", textAlign: "center" }}>
                Your life has an operating system.
              </span>
              <span className="rv" data-stagger="160" style={{ display: "block", fontSize: 42, lineHeight: 1.15, fontStyle: "italic", color: "#9E8456", textAlign: "center" }}>
                Most people are running theirs blind.
              </span>
            </h2>
            <div className="rv" data-stagger="240" style={{ width: 32, height: 1, background: "rgba(158,132,86,0.30)", margin: "0 auto 48px" }} />
            <p className="rv" data-stagger="300" style={{ fontSize: 16, color: "rgba(28,24,20,0.55)", lineHeight: 1.78, maxWidth: 560, margin: "0 auto 20px", textAlign: "center" }}>
              Haiven is the first instrument built to read it. Not a journal. Not a tracker. A measurement instrument for the gap between the person you intend to be and the one your data shows you are.
            </p>
            <p className="rv" data-stagger="380" style={{ fontSize: 16, color: "rgba(28,24,20,0.55)", lineHeight: 1.78, maxWidth: 560, margin: "0 auto 20px", textAlign: "center" }}>
              Real science. Not woo. Built on what behavioral research actually knows about humans.
            </p>
            <p className="rv" data-stagger="460" style={{ fontSize: 16, color: "rgba(28,24,20,0.55)", lineHeight: 1.78, maxWidth: 560, margin: "0 auto 48px", textAlign: "center" }}>
              This is early. The science is real. The founding cohort is how we calibrate it.
            </p>
            <span className="rv" data-stagger="540" style={{ display: "block", fontFamily: display, fontStyle: "italic", fontSize: 28, color: "#9E8456", marginTop: 0, textAlign: "center" }}>
              You're the signal.
            </span>
          </div>
        </section>

        {/* ===== EARLY ACCESS CTA ===== */}
        <section id="early-access" style={{ background: P.bg, borderTop: "0.5px solid rgba(28,24,20,0.07)", position: "relative", overflow: "hidden" }}>
          <div className="section-pad" style={{ textAlign: "center", maxWidth: 640, position: "relative", zIndex: 1 }}>
            <div className="rv" style={{ fontSize: 13, letterSpacing: "0.20em", textTransform: "uppercase", color: "#9E8456", fontWeight: 400, marginBottom: 20 }}>Early Access</div>
            <h2 className="rv" data-stagger="80" style={{ fontFamily: display, fontSize: "clamp(2.8rem, 5vw, 5rem)", color: P.text, marginBottom: 20, fontWeight: 400, lineHeight: 1.06 }}>
              A clearer way to recover<br />
              is being <span style={{ fontStyle: "italic", color: P.gold }}>built.</span>
            </h2>
            <p className="rv" data-stagger="160" style={{ fontSize: "0.92rem", color: P.body, fontWeight: 300, lineHeight: 1.8, marginBottom: 40 }}>
              Haiven is in private beta. We're building carefully and opening selectively. Join the list to be considered for early access.
            </p>

            {!submitted ? (
              <form className="rv" data-stagger="240" onSubmit={async (e) => {
                e.preventDefault();
                setFormError("");
                if (!firstName.trim() || !lastName.trim() || !email.trim()) {
                  setFormError("All fields are required.");
                  return;
                }
                setSubmitting(true);
                try {
                  const res = await fetch("https://api.haiven.co/api/v1/waitlist", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      first_name: firstName.trim(),
                      last_name: lastName.trim(),
                      email: email.trim().toLowerCase(),
                    }),
                  });
                  const data = await res.json();
                  if (!res.ok) {
                    setFormError(data.detail || "Something went wrong.");
                  } else if (data.status === "duplicate") {
                    setFormError("You're already on the list.");
                  } else {
                    setSubmitted(true);
                  }
                } catch (err) {
                  setFormError("Connection error. Please try again.");
                } finally {
                  setSubmitting(false);
                }
              }} style={{ maxWidth: 480, margin: "0 auto" }}>
                <input type="text" placeholder="First name" value={firstName}
                  onChange={(e) => setFirstName(e.target.value)} required
                  style={{
                    width: "100%", padding: "12px 16px", marginBottom: 10,
                    borderRadius: 4, border: "0.5px solid rgba(28,24,20,0.18)",
                    fontSize: 14, fontFamily: sans, outline: "none",
                    color: P.text, background: P.bg,
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#9E8456"}
                  onBlur={(e) => e.target.style.borderColor = "rgba(28,24,20,0.18)"}
                />
                <input type="text" placeholder="Last name" value={lastName}
                  onChange={(e) => setLastName(e.target.value)} required
                  style={{
                    width: "100%", padding: "12px 16px", marginBottom: 10,
                    borderRadius: 4, border: "0.5px solid rgba(28,24,20,0.18)",
                    fontSize: 14, fontFamily: sans, outline: "none",
                    color: P.text, background: P.bg,
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#9E8456"}
                  onBlur={(e) => e.target.style.borderColor = "rgba(28,24,20,0.18)"}
                />
                <input type="email" placeholder="Email address" value={email}
                  onChange={(e) => setEmail(e.target.value)} required
                  style={{
                    width: "100%", padding: "12px 16px", marginBottom: 10,
                    borderRadius: 4, border: "0.5px solid rgba(28,24,20,0.18)",
                    fontSize: 14, fontFamily: sans, outline: "none",
                    color: P.text, background: P.bg,
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#9E8456"}
                  onBlur={(e) => e.target.style.borderColor = "rgba(28,24,20,0.18)"}
                />
                {formError && (
                  <p style={{ fontSize: 13, color: "#9E8456", marginBottom: 10 }}>{formError}</p>
                )}
                <button type="submit" disabled={submitting} style={{
                  width: "100%", padding: 13, fontSize: 14,
                  borderRadius: 4, border: "none", cursor: "pointer",
                  background: "#1C1814", color: "#F8F6F2",
                  fontFamily: sans, opacity: submitting ? 0.6 : 1,
                  transition: "opacity 0.25s",
                }}>{submitting ? "Submitting..." : "Apply for the founding cohort"}</button>
              </form>
            ) : (
              <div className="rv rv-visible" style={{ textAlign: "center" }}>
                <p style={{ fontFamily: display, fontStyle: "italic", fontSize: 22, color: "#1C1814", marginBottom: 8 }}>
                  You're on the list.
                </p>
                <p style={{ fontSize: 14, color: "rgba(28,24,20,0.50)" }}>
                  We're opening carefully. We'll be in touch.
                </p>
              </div>
            )}
            <p className="rv" data-stagger="320" style={{ fontSize: 13, color: "rgba(28,24,20,0.50)", marginTop: 30, fontWeight: 300 }}>
              No spam. No noise. Just signal.
            </p>
            <p className="rv" data-stagger="360" style={{ fontSize: 12, color: "rgba(28,24,20,0.35)", textAlign: "center", marginTop: 12, fontWeight: 300 }}>
              Bringing Haiven to your practice?{" "}
              <a href="mailto:hello@haiven.co" style={{ color: "rgba(158,132,86,0.65)", textDecoration: "none" }}>
                hello@haiven.co
              </a>
            </p>
          </div>
        </section>

        {/* ===== FOOTER ===== */}
        <footer style={{
          borderTop: P.divider,
          padding: "28px 36px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 16,
        }}>
          <img src="/logo-gold.png" alt="Haiven" style={{ height: 20, width: "auto", opacity: 0.5 }} />
          <div style={{ display: "flex", gap: 24 }}>
            <a href="https://haiven.co/privacy" style={{ fontSize: "0.78rem", color: P.eyebrow, fontWeight: 300 }}>Privacy</a>
            <a href="https://api.haiven.co/terms" style={{ fontSize: "0.78rem", color: P.eyebrow, fontWeight: 300 }}>Terms</a>
          </div>
          <span style={{ fontSize: "0.72rem", color: P.eyebrow, fontWeight: 300 }}>&copy; 2026 Haiven</span>
        </footer>
      </div>
    </>
  );
}
